// ══════════════════════════════════════════════════════════════════
// CartHub — Price Alert Design: widget PDP (copy, tema, icona).
// Salva su shop_settings via PUT /api/settings/:shopId (JWT).
// Stato attivazione plugin WP è in sola lettura (sync webhook).
// Testi secondari: var(--text2) / anteprima eredita colore widget (no grigio fisso).
// ══════════════════════════════════════════════════════════════════
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { LegendInfoIcon, useMediaMinWidth, apiRequest, Ic } from "../shared";
import { paWidgetValueIfNotItalianServerDefault } from "../constants/paWidgetServerTextDefaultsIt";
import { PRICE_ALERT_ICON_KEYS, PriceAlertWidgetIconPreview } from "./priceAlertWidgetIcons";

function wpCarthubUrl(domain) {
  if (!domain || typeof domain !== "string") return "";
  const host = domain.replace(/^https?:\/\//i, "").split("/")[0].trim();
  if (!host) return "";
  return `https://${host}/wp-admin/admin.php?page=carthub`;
}

/** Campo vuoto → usa placeholder (default per lingua, allineato al ripristino). */
function effectiveText(value, placeholder) {
  const v = String(value ?? "").trim();
  return v || placeholder;
}

function effectiveInt(value, placeholderDigits, min, max, hardFallback) {
  const s = String(value ?? "").trim();
  const n = s === "" ? parseInt(String(placeholderDigits), 10) : parseInt(s, 10);
  if (!Number.isFinite(n)) return hardFallback;
  return Math.min(max, Math.max(min, n));
}

function normalizeHex6(raw, fallback) {
  const t = String(raw == null ? "" : raw).trim();
  if (/^#[0-9A-Fa-f]{6}$/i.test(t)) return `#${t.slice(1).toLowerCase()}`;
  if (/^#[0-9A-Fa-f]{3}$/i.test(t)) {
    const h = t.slice(1);
    return `#${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}`.toLowerCase();
  }
  return fallback;
}

function ColorField({ label, value, onChange, fallbackHex }) {
  const safe = normalizeHex6(value, fallbackHex);
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 4 }}>{label}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <input
          type="color"
          value={safe}
          onChange={(e) => onChange(normalizeHex6(e.target.value, fallbackHex))}
          title={label}
          style={{
            width: 48,
            height: 36,
            padding: 0,
            border: "1px solid var(--border2)",
            borderRadius: 8,
            cursor: "pointer",
          }}
        />
        <input
          className="ch-input"
          style={{ maxWidth: 120 }}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={fallbackHex}
        />
      </div>
    </div>
  );
}

function WidgetPreview({
  displayTitle,
  displayPlaceholder,
  displayCta,
  footerHint,
  displaySuccess,
  bg,
  textColor,
  borderColor,
  borderW,
  radius,
  btnBg,
  btnText,
  successColor,
  showIcon,
  iconKey,
}) {
  const innerR = Math.min(Number(radius) || 0, 6);
  return (
    <div
      className="ch-pa-widget-preview"
      style={{
        marginTop: 8,
        padding: "14px 16px",
        background: bg,
        borderRadius: radius,
        border: `${borderW}px solid ${borderColor}`,
        color: textColor,
        maxWidth: 420,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        {showIcon ? (
          <div
            className="ch-pa-widget-preview__icon-slot"
            style={{ flexShrink: 0, width: 28, marginTop: 2, color: textColor }}
            aria-hidden="true"
          >
            <PriceAlertWidgetIconPreview iconKey={iconKey} size={26} />
          </div>
        ) : null}
        <div style={{ flex: 1, minWidth: 0 }}>
          <p className="ch-pa-widget-preview__title">{displayTitle}</p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <input
              type="text"
              readOnly
              placeholder={displayPlaceholder}
              style={{
                flex: 1,
                minWidth: 120,
                padding: "8px 12px",
                border: `1px solid ${borderColor}`,
                borderRadius: innerR,
                fontSize: 16,
                background: "#fff",
                color: "#111",
              }}
            />
            <button
              type="button"
              style={{
                padding: "8px 16px",
                background: btnBg,
                color: btnText,
                border: "none",
                borderRadius: innerR,
                fontSize: 15,
                fontWeight: 500,
                cursor: "default",
              }}
            >
              {displayCta}
            </button>
          </div>
          <p className="ch-pa-widget-preview__footer">{footerHint}</p>
          <p className="ch-pa-widget-preview__success" style={{ color: successColor, display: "none" }}>
            {displaySuccess}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function PriceAlertSetup({ shop, onOpenStats }) {
  const SAVE_FLASH_STORAGE_KEY = "carthub_pa_saved_until";
  const { t, i18n } = useTranslation("priceAlertSetup");
  const isDesktop = useMediaMinWidth(900);
  const [mobileOpen, setMobileOpen] = useState(false);
  const shopId = shop?.id;
  const adminUrl = wpCarthubUrl(shop?.domain);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [pluginEnabledCloud, setPluginEnabledCloud] = useState(false);
  const savedHideTimerRef = useRef(null);

  const [title, setTitle] = useState("");
  const [emailPh, setEmailPh] = useState("");
  const [cta, setCta] = useState("");
  const [submitting, setSubmitting] = useState("");
  const [footer, setFooter] = useState("");
  const [success, setSuccess] = useState("");
  const [bg, setBg] = useState("#f9fafb");
  const [textC, setTextC] = useState("#333333");
  const [borderC, setBorderC] = useState("#e5e7eb");
  const [borderW, setBorderW] = useState(1);
  const [radius, setRadius] = useState(8);
  const [btnBg, setBtnBg] = useState("#0A85CF");
  const [btnText, setBtnText] = useState("#ffffff");
  const [successC, setSuccessC] = useState("#22c55e");
  const [showIcon, setShowIcon] = useState(false);
  const [iconKey, setIconKey] = useState("bell");

  /** Sincronizza form da una riga `shop_settings` (GET o body del PUT). */
  const applyRowFromServer = useCallback((d) => {
    if (d == null || typeof d !== "object" || !Object.prototype.hasOwnProperty.call(d, "shop_id")) {
      return false;
    }
    setPluginEnabledCloud(!!d.price_alert_plugin_enabled);
    setTitle(paWidgetValueIfNotItalianServerDefault("price_alert_widget_title", d.price_alert_widget_title));
    setEmailPh(
      paWidgetValueIfNotItalianServerDefault("price_alert_widget_email_placeholder", d.price_alert_widget_email_placeholder)
    );
    setCta(paWidgetValueIfNotItalianServerDefault("price_alert_widget_cta_label", d.price_alert_widget_cta_label));
    setSubmitting(
      paWidgetValueIfNotItalianServerDefault("price_alert_widget_submitting_label", d.price_alert_widget_submitting_label)
    );
    setFooter(paWidgetValueIfNotItalianServerDefault("price_alert_widget_footer_text", d.price_alert_widget_footer_text));
    setSuccess(paWidgetValueIfNotItalianServerDefault("price_alert_widget_success_text", d.price_alert_widget_success_text));
    setBg(d.price_alert_widget_background ?? "#f9fafb");
    setTextC(d.price_alert_widget_text_color ?? "#333333");
    setBorderC(d.price_alert_widget_border_color ?? "#e5e7eb");
    setBorderW(d.price_alert_widget_border_width_px ?? 1);
    setRadius(d.price_alert_widget_border_radius_px ?? 8);
    setBtnBg(d.price_alert_widget_button_background ?? "#0A85CF");
    setBtnText(d.price_alert_widget_button_text_color ?? "#ffffff");
    setSuccessC(d.price_alert_widget_success_color ?? "#22c55e");
    setShowIcon(!!d.price_alert_widget_show_icon);
    setIconKey(d.price_alert_widget_icon_key || "bell");
    return true;
  }, []);

  const load = () => {
    if (!shopId) return Promise.resolve();
    setLoading(true);
    return apiRequest(`/settings/${shopId}`)
      .then((d) => {
        if (!applyRowFromServer(d)) {
          console.error("price alert settings load: risposta non valida");
        }
      })
      .catch((e) => {
        console.error("price alert settings load", e);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const flashSaved = useCallback(() => {
    if (savedHideTimerRef.current) {
      clearTimeout(savedHideTimerRef.current);
      savedHideTimerRef.current = null;
    }
    const savedUntil = Date.now() + 2800;
    try {
      sessionStorage.setItem(SAVE_FLASH_STORAGE_KEY, String(savedUntil));
    } catch (_) {}
    setSaved(true);
    savedHideTimerRef.current = setTimeout(() => {
      setSaved(false);
      try {
        sessionStorage.removeItem(SAVE_FLASH_STORAGE_KEY);
      } catch (_) {}
      savedHideTimerRef.current = null;
    }, 2800);
  }, []);

  useEffect(() => {
    try {
      const untilRaw = sessionStorage.getItem(SAVE_FLASH_STORAGE_KEY);
      const untilTs = Number(untilRaw || "0");
      if (Number.isFinite(untilTs) && untilTs > Date.now()) {
        setSaved(true);
        const left = untilTs - Date.now();
        savedHideTimerRef.current = setTimeout(() => {
          setSaved(false);
          try {
            sessionStorage.removeItem(SAVE_FLASH_STORAGE_KEY);
          } catch (_) {}
          savedHideTimerRef.current = null;
        }, left);
      } else if (untilRaw) {
        sessionStorage.removeItem(SAVE_FLASH_STORAGE_KEY);
      }
    } catch (_) {}
    return () => {
      if (savedHideTimerRef.current) clearTimeout(savedHideTimerRef.current);
    };
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional
  }, [shopId]);

  useEffect(() => {
    if (!isDesktop) setMobileOpen(false);
  }, [isDesktop]);

  const footerPreview = useMemo(() => {
    const base = effectiveText(footer, t("placeholders.footer"));
    const privacyToken = "{privacy}";
    if (!base.includes(privacyToken)) return base;
    return base.split(privacyToken).join(t("preview.privacyLinkStub"));
  }, [footer, t, i18n.language]);

  const save = async () => {
    if (!shopId) return;
    setSaving(true);
    setSaveError("");
    try {
      const bw = effectiveInt(borderW, t("placeholders.borderWidth"), 0, 8, 1);
      const br = effectiveInt(radius, t("placeholders.borderRadius"), 0, 24, 8);
      const updated = await apiRequest(`/settings/${shopId}`, {
        method: "PUT",
        body: JSON.stringify({
          price_alert_widget_title: effectiveText(title, t("placeholders.title")),
          price_alert_widget_email_placeholder: effectiveText(emailPh, t("placeholders.emailPlaceholder")),
          price_alert_widget_cta_label: effectiveText(cta, t("placeholders.cta")),
          price_alert_widget_submitting_label: effectiveText(submitting, t("placeholders.submitting")),
          price_alert_widget_footer_text: effectiveText(footer, t("placeholders.footer")),
          price_alert_widget_success_text: effectiveText(success, t("placeholders.success")),
          price_alert_widget_background: normalizeHex6(bg, "#f9fafb"),
          price_alert_widget_text_color: normalizeHex6(textC, "#333333"),
          price_alert_widget_border_color: normalizeHex6(borderC, "#e5e7eb"),
          price_alert_widget_border_width_px: bw,
          price_alert_widget_border_radius_px: br,
          price_alert_widget_button_background: normalizeHex6(btnBg, "#0A85CF"),
          price_alert_widget_button_text_color: normalizeHex6(btnText, "#ffffff"),
          price_alert_widget_success_color: normalizeHex6(successC, "#22c55e"),
          price_alert_widget_show_icon: showIcon,
          price_alert_widget_icon_key: iconKey,
        }),
      });
      if (!applyRowFromServer(updated)) {
        await load();
      }
      flashSaved();
    } catch (e) {
      console.error("price alert save", e);
      setSaveError(e?.message || t("form.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const restoreDefaults = async () => {
    if (!shopId) return;
    if (!window.confirm(t("reset.confirm"))) return;
    setSaving(true);
    setSaveError("");
    try {
      const updated = await apiRequest(`/settings/${shopId}`, {
        method: "PUT",
        body: JSON.stringify({ price_alert_widget_restore_defaults: true }),
      });
      if (!applyRowFromServer(updated)) {
        await load();
      }
      flashSaved();
    } catch (e) {
      console.error("price alert restore", e);
      setSaveError(e?.message || t("form.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const panelId = "price-alert-interface-help-panel";
  const showPanel = isDesktop || mobileOpen;

  const saveBtnBase = {
    background: "var(--accent)",
    color: "#fff",
    border: "1px solid var(--accent)",
  };

  if (!shopId) return null;

  const helpLegendSection = (
    <section className="ch-help-legend ch-help-legend--embed" style={{ marginBottom: 20 }} aria-label={t("legend.aria")}>
      <div className="ch-help-legend__accent" aria-hidden="true" />
      {!isDesktop && (
        <button
          type="button"
          className="ch-help-legend__summary"
          aria-expanded={mobileOpen}
          aria-controls={panelId}
          onClick={() => setMobileOpen((o) => !o)}
        >
          <span className="ch-help-legend__sum-icon">
            <LegendInfoIcon size={18} />
          </span>
          <span>
            {t("legend.summaryTitle")}
            <span className="ch-help-legend__sum-hint">{t("legend.summaryHint")}</span>
          </span>
          <span className="ch-help-legend__chev" aria-hidden="true" />
        </button>
      )}
      {showPanel && (
        <div id={panelId} className="ch-help-legend__panel">
          <header className="ch-help-legend__head ch-help-legend__head--mobile-only">
            <div className="ch-help-legend__head-icon" aria-hidden="true">
              <LegendInfoIcon />
            </div>
            <div>
              <h2 className="ch-help-legend__title">{t("legend.summaryTitle")}</h2>
              <p className="ch-help-legend__sub">{t("legend.summaryHint")}</p>
            </div>
          </header>

          <div className="ch-help-legend__desktop">
            <div className="ch-help-legend__desktop-intro">
              <div className="ch-help-legend__head-icon" aria-hidden="true">
                <LegendInfoIcon size={20} />
              </div>
              <div>
                <h2 className="ch-help-legend__title">{t("legend.summaryTitle")}</h2>
                <p className="ch-help-legend__sub">{t("legend.summaryHint")}</p>
              </div>
            </div>
            <div style={{ padding: "14px 18px", fontSize: 14, lineHeight: 1.5, overflow: "auto", color: "var(--text2)" }}>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 600, color: "var(--text)", marginBottom: 6 }}>{t("wp.title")}</div>
                <p className="ch-help-legend__text" style={{ margin: 0 }}>
                  {t("wp.body")}
                </p>
                {adminUrl ? (
                  <p className="ch-help-legend__text" style={{ margin: "8px 0 0" }}>
                    <a href={adminUrl} target="_blank" rel="noopener noreferrer" style={{ color: "var(--link)", fontWeight: 500 }}>
                      {t("wp.linkLabel")}
                    </a>
                  </p>
                ) : null}
              </div>
              <div>
                <div style={{ fontWeight: 600, color: "var(--text)", marginBottom: 6 }}>{t("cloud.title")}</div>
                <p className="ch-help-legend__text" style={{ margin: 0 }}>
                  {t("cloud.body")}
                </p>
              </div>
            </div>
          </div>

          <div className="ch-help-legend__list" style={{ paddingTop: 8 }}>
            <div className="ch-help-legend__row">
              <div className="ch-help-legend__rail ch-help-legend__rail--neutral" aria-hidden="true" />
              <div className="ch-help-legend__body">
                <div style={{ fontWeight: 600, color: "var(--text)" }}>{t("wp.title")}</div>
                <p className="ch-help-legend__text">{t("wp.body")}</p>
                {adminUrl ? (
                  <p className="ch-help-legend__text" style={{ margin: 0 }}>
                    <a href={adminUrl} target="_blank" rel="noopener noreferrer" style={{ color: "var(--link)", fontWeight: 500 }}>
                      {t("wp.linkLabel")}
                    </a>
                  </p>
                ) : null}
              </div>
            </div>
            <div className="ch-help-legend__row">
              <div className="ch-help-legend__rail ch-help-legend__rail--neutral" aria-hidden="true" />
              <div className="ch-help-legend__body">
                <div style={{ fontWeight: 600, color: "var(--text)" }}>{t("cloud.title")}</div>
                <p className="ch-help-legend__text">{t("cloud.body")}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );

  return (
    <div>
      {helpLegendSection}

      <div className="ch-card" style={{ marginBottom: 16 }}>
        <h2 style={{ display: "flex", alignItems: "center", gap: 10, color: "var(--text)" }}>
          <span style={{ color: "var(--accent)", display: "flex" }}>{Ic.bell}</span>
          {t("page.title")}
        </h2>
        <p style={{ fontSize: 14, color: "var(--text2)", marginBottom: 16, lineHeight: 1.55 }}>{t("page.intro")}</p>

        {loading ? (
          <div style={{ fontSize: 14, color: "var(--text3)", padding: "12px 0" }}>{t("page.loading")}</div>
        ) : (
          <>
            {saved ? (
              <div className="ch-pa-save-banner" role="status" aria-live="polite">
                {Ic.check}
                {t("form.saved")}
              </div>
            ) : null}
            {saveError ? <div className="ch-pa-save-error" role="alert">{saveError}</div> : null}
            <h3 style={{ fontSize: 15, fontWeight: 600, margin: "0 0 10px", color: "var(--text)" }}>{t("page.pluginStatusTitle")}</h3>
            <div
              style={{
                marginBottom: 20,
                padding: "12px 14px",
                background: "var(--surface)",
                border: "1px solid var(--border2)",
                borderRadius: 8,
                fontSize: 14,
                color: "var(--text)",
                lineHeight: 1.55,
              }}
            >
              <strong style={{ color: "var(--text)" }}>{t("page.pluginStatusHeading")}</strong>
              <div style={{ marginTop: 8 }}>
                {t("page.pluginStatusLabel")}{" "}
                <span
                  className={pluginEnabledCloud ? "ch-status success" : "ch-status neutral"}
                  style={{ display: "inline-flex", marginLeft: 4 }}
                >
                  <span className="status-dot" />
                  {pluginEnabledCloud ? t("page.pluginActive") : t("page.pluginInactive")}
                </span>
              </div>
              <p style={{ margin: "10px 0 0", fontSize: 13, color: "var(--text2)" }}>
                {t("page.pluginStatusHint")}{" "}
                {adminUrl ? (
                  <a href={adminUrl} target="_blank" rel="noopener noreferrer" style={{ color: "var(--link)", fontWeight: 500 }}>
                    {t("page.pluginWpLink")}
                  </a>
                ) : (
                  <strong>{t("page.pluginWpStrong")}</strong>
                )}
                {t("page.pluginStatusHint2")}
              </p>
            </div>

            <p style={{ fontSize: 13, color: "var(--text2)", marginBottom: 16, lineHeight: 1.5 }}>{t("page.cacheHint")}</p>

            <div style={{ display: "grid", gridTemplateColumns: isDesktop ? "1fr 1fr" : "1fr", gap: 24 }}>
              <div>
                <h3 style={{ fontSize: 15, fontWeight: 600, margin: "0 0 12px", color: "var(--text)" }}>{t("form.sectionCopy")}</h3>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 4 }}>{t("form.title")}</div>
                  <input
                    className="ch-input"
                    style={{ width: "100%" }}
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder={t("placeholders.title")}
                  />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 4 }}>{t("form.emailPlaceholder")}</div>
                  <input
                    className="ch-input"
                    style={{ width: "100%" }}
                    value={emailPh}
                    onChange={(e) => setEmailPh(e.target.value)}
                    placeholder={t("placeholders.emailPlaceholder")}
                  />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 4 }}>{t("form.cta")}</div>
                  <input
                    className="ch-input"
                    style={{ width: "100%" }}
                    value={cta}
                    onChange={(e) => setCta(e.target.value)}
                    placeholder={t("placeholders.cta")}
                  />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 4 }}>{t("form.submitting")}</div>
                  <input
                    className="ch-input"
                    style={{ width: "100%" }}
                    value={submitting}
                    onChange={(e) => setSubmitting(e.target.value)}
                    placeholder={t("placeholders.submitting")}
                  />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 4 }}>{t("form.success")}</div>
                  <input
                    className="ch-input"
                    style={{ width: "100%" }}
                    value={success}
                    onChange={(e) => setSuccess(e.target.value)}
                    placeholder={t("placeholders.success")}
                  />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 4 }}>{t("form.footer")}</div>
                  <textarea
                    className="ch-input"
                    style={{ width: "100%", minHeight: 72, resize: "vertical" }}
                    value={footer}
                    onChange={(e) => setFooter(e.target.value)}
                    placeholder={t("placeholders.footer")}
                  />
                  <p className="ch-help-legend__text" style={{ margin: "6px 0 0", fontSize: 12, color: "var(--text2)" }}>
                    {t("form.footerPrivacyHelp")}
                  </p>
                </div>

                <h3 style={{ fontSize: 15, fontWeight: 600, margin: "20px 0 12px", color: "var(--text)" }}>{t("form.sectionTheme")}</h3>
                <ColorField label={t("form.colorBg")} value={bg} onChange={setBg} fallbackHex="#f9fafb" />
                <ColorField label={t("form.colorText")} value={textC} onChange={setTextC} fallbackHex="#333333" />
                <ColorField label={t("form.colorBorder")} value={borderC} onChange={setBorderC} fallbackHex="#e5e7eb" />
                <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 12 }}>
                  <div>
                    <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 4 }}>{t("form.borderWidth")}</div>
                    <input
                      className="ch-input"
                      type="number"
                      min={0}
                      max={8}
                      value={borderW}
                      onChange={(e) => setBorderW(e.target.value === "" ? "" : e.target.value)}
                      placeholder={t("placeholders.borderWidth")}
                    />
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 4 }}>{t("form.borderRadius")}</div>
                    <input
                      className="ch-input"
                      type="number"
                      min={0}
                      max={24}
                      value={radius}
                      onChange={(e) => setRadius(e.target.value === "" ? "" : e.target.value)}
                      placeholder={t("placeholders.borderRadius")}
                    />
                  </div>
                </div>
                <p className="ch-help-legend__text" style={{ margin: "0 0 12px", fontSize: 12, color: "var(--text2)" }}>
                  {t("form.radiusHelp")}
                </p>
                <ColorField label={t("form.colorBtnBg")} value={btnBg} onChange={setBtnBg} fallbackHex="#0A85CF" />
                <ColorField label={t("form.colorBtnText")} value={btnText} onChange={setBtnText} fallbackHex="#ffffff" />
                <ColorField label={t("form.colorSuccess")} value={successC} onChange={setSuccessC} fallbackHex="#22c55e" />

                <h3 style={{ fontSize: 15, fontWeight: 600, margin: "20px 0 12px", color: "var(--text)" }}>{t("form.sectionIcon")}</h3>
                <label style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, cursor: "pointer", color: "var(--text)" }}>
                  <input type="checkbox" checked={showIcon} onChange={(e) => setShowIcon(e.target.checked)} />
                  <span style={{ fontSize: 14 }}>{t("form.showIcon")}</span>
                </label>
                {showIcon ? (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                    {PRICE_ALERT_ICON_KEYS.map((k) => (
                      <button
                        key={k}
                        type="button"
                        className="ch-pa-icon-btn"
                        onClick={() => {
                          setIconKey(k);
                          setSaveError("");
                        }}
                        style={{
                          padding: 8,
                          border: iconKey === k ? "2px solid var(--accent)" : "1px solid var(--border2)",
                          borderRadius: 8,
                          background: iconKey === k ? "var(--accent-bg)" : "var(--surface)",
                          cursor: "pointer",
                        }}
                        title={k}
                        aria-pressed={iconKey === k}
                      >
                        <PriceAlertWidgetIconPreview iconKey={k} size={28} />
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>

              <div className={isDesktop ? "ch-dashboard-sticky-preview" : undefined}>
                <h3 style={{ fontSize: 15, fontWeight: 600, margin: "0 0 12px", color: "var(--text)" }}>{t("preview.title")}</h3>
                <WidgetPreview
                  displayTitle={effectiveText(title, t("placeholders.title"))}
                  displayPlaceholder={effectiveText(emailPh, t("placeholders.emailPlaceholder"))}
                  displayCta={effectiveText(cta, t("placeholders.cta"))}
                  footerHint={footerPreview}
                  displaySuccess={effectiveText(success, t("placeholders.success"))}
                  bg={normalizeHex6(bg, "#f9fafb")}
                  textColor={normalizeHex6(textC, "#333333")}
                  borderColor={normalizeHex6(borderC, "#e5e7eb")}
                  borderW={effectiveInt(borderW, t("placeholders.borderWidth"), 0, 8, 1)}
                  radius={effectiveInt(radius, t("placeholders.borderRadius"), 0, 24, 8)}
                  btnBg={normalizeHex6(btnBg, "#0A85CF")}
                  btnText={normalizeHex6(btnText, "#ffffff")}
                  successColor={normalizeHex6(successC, "#22c55e")}
                  showIcon={showIcon}
                  iconKey={iconKey}
                />
              </div>
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 24, alignItems: "center" }}>
              <button
                type="button"
                className="ch-btn"
                style={saveBtnBase}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "var(--accent-hover)";
                  e.currentTarget.style.borderColor = "var(--accent-hover)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "var(--accent)";
                  e.currentTarget.style.borderColor = "var(--accent)";
                }}
                onClick={save}
                disabled={saving}
              >
                {saving ? t("form.saving") : t("form.save")}
              </button>
              <button type="button" className="ch-btn" onClick={restoreDefaults} disabled={saving}>
                {t("reset.button")}
              </button>
              {saved ? (
                <span className="ch-pa-save-inline" role="status" aria-live="polite">
                  {Ic.check}
                  {t("form.saved")}
                </span>
              ) : null}
            </div>
          </>
        )}
      </div>

      {typeof onOpenStats === "function" ? (
        <button
          type="button"
          className="pill"
          style={{ cursor: "pointer", border: "1px solid var(--border2)", marginBottom: 8 }}
          onClick={onOpenStats}
        >
          {t("openStats")}
        </button>
      ) : null}
    </div>
  );
}
